import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { X, Wallet, Eye, Plug } from 'lucide-react';
import { WALLET_PROVIDER_INFO, WalletProviderType } from '../../types/multi-wallet';
import { toast } from 'sonner';

interface AddWalletModalProps {
  onClose: () => void;
}

export function AddWalletModal({ onClose }: AddWalletModalProps) {
  const [mode, setMode] = useState<'connect' | 'watch'>('connect');
  const [watchAddress, setWatchAddress] = useState('');
  const [watchLabel, setWatchLabel] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);

  const connectableWallets: WalletProviderType[] = ['metamask', 'thor_wallet', 'walletconnect', 'coinbase', 'ledger'];

  const handleConnectWallet = async (type: WalletProviderType) => {
    setIsConnecting(true);
    try {
      // Here we would implement actual wallet connection logic
      console.log('Connecting wallet:', type);

      // Simulate connection delay
      await new Promise((resolve) => setTimeout(resolve, 1500));

      toast.success(`${WALLET_PROVIDER_INFO[type].name} conectada exitosamente`);
      onClose();
    } catch (error) {
      toast.error('Error al conectar la wallet');
      console.error(error);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleAddWatchOnly = () => {
    if (!watchAddress || !watchAddress.startsWith('0x') || watchAddress.length !== 42) {
      toast.error('Por favor ingresa una direccion de wallet valida');
      return;
    }

    // Here we would add the watch-only wallet to the user's list
    console.log('Adding watch-only wallet:', { address: watchAddress, label: watchLabel });

    toast.success('Wallet agregada en modo lectura');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Agregar Wallet</CardTitle>
            <CardDescription>Conecta una nueva wallet o agrega una en modo lectura</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Mode Selection */}
          <div className="grid grid-cols-2 gap-4">
            <Button
              variant={mode === 'connect' ? 'default' : 'outline'}
              onClick={() => setMode('connect')}
              className="h-auto py-4 flex flex-col items-center gap-2"
            >
              <Plug className="h-6 w-6" />
              <span>Conectar Wallet</span>
              <span className="text-xs opacity-70">Control total</span>
            </Button>
            <Button
              variant={mode === 'watch' ? 'default' : 'outline'}
              onClick={() => setMode('watch')}
              className="h-auto py-4 flex flex-col items-center gap-2"
            >
              <Eye className="h-6 w-6" />
              <span>Solo Lectura</span>
              <span className="text-xs opacity-70">Ver balances</span>
            </Button>
          </div>

          {mode === 'connect' ? (
            /* Connect Wallet Options */
            <div className="space-y-3">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Selecciona tu proveedor de wallet:
              </p>
              {connectableWallets.map((walletType) => {
                const info = WALLET_PROVIDER_INFO[walletType];
                const isAvailable = walletType === 'metamask' || walletType === 'thor_wallet';

                return (
                  <Button
                    key={walletType}
                    variant="outline"
                    className="w-full justify-start h-auto py-4"
                    disabled={!isAvailable || isConnecting}
                    onClick={() => handleConnectWallet(walletType)}
                  >
                    <span className="text-2xl mr-4">{info.icon}</span>
                    <div className="text-left flex-1">
                      <p className="font-medium">{info.name}</p>
                      {!isAvailable && (
                        <p className="text-xs text-slate-500">Proximamente</p>
                      )}
                    </div>
                    {isConnecting && (
                      <div className="animate-spin h-4 w-4 border-2 border-violet-500 border-t-transparent rounded-full" />
                    )}
                  </Button>
                );
              })}
            </div>
          ) : (
            /* Watch-Only Wallet */
            <div className="space-y-4">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Agrega cualquier direccion de wallet para ver sus balances sin necesidad de conectarla.
              </p>

              <div className="space-y-2">
                <Label htmlFor="address">Direccion de Wallet</Label>
                <Input
                  id="address"
                  placeholder="0x..."
                  value={watchAddress}
                  onChange={(e) => setWatchAddress(e.target.value)}
                  className="font-mono"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="label">Etiqueta (opcional)</Label>
                <Input
                  id="label"
                  placeholder="Ej: Mi wallet de Binance"
                  value={watchLabel}
                  onChange={(e) => setWatchLabel(e.target.value)}
                />
              </div>

              <Button
                className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
                onClick={handleAddWatchOnly}
              >
                <Eye className="mr-2 h-4 w-4" />
                Agregar en Modo Lectura
              </Button>

              <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  <strong>Nota:</strong> Las wallets en modo lectura solo permiten ver balances y transacciones.
                  No podras realizar operaciones con ellas.
                </p>
              </div>
            </div>
          )}

          {/* Info */}
          <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
            <p className="text-xs text-slate-500 text-center">
              Puedes agregar multiples wallets de diferentes redes y proveedores.
              Tus fondos permanecen seguros en tu wallet.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
